// @ts-check
/**
 * Opens the AI Report Explainer panel. Independent of the panel, so it can move - or a second entry point
 * be added - without touching it.
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { LuSparkles } from 'react-icons/lu';

import { TestableButton } from '@components/general/TestableComponents';
import { useAIPanel } from '@components/general/aiPanel/aiPanelContext';
import OrganisationSummaryPanel from './OrganisationSummaryPanel.jsx';

/** @typedef {import('@datahooks/ProjectsListContext').BasicProject} BasicProject */

/**
 * Hook to load the AI Explainer panel.
 * 
 * Shared by the button and the share-link route, so the two entry points cannot drift.
 *
 * @returns {(args: { organisationName?: string, projects?: BasicProject[], linkedOrgUnavailable?: boolean }) => void}
 */
export function useOpenExplainer() {
  const { openPanel } = useAIPanel();
  return useCallback(({ organisationName = '', projects = [], linkedOrgUnavailable = false }) => {
    openPanel(
      <OrganisationSummaryPanel
        organisationName={organisationName}
        projects={projects}
        linkedOrgUnavailable={linkedOrgUnavailable}
      />
    );
  }, [openPanel]);
}


/**
 * Opens the AI Explainer panel for the given organisation and projects.
 *
 *  @param {{ organisationName?: string, projects?: BasicProject[] }} props
 *  @returns {JSX.Element}
 */
const AIExplainerButton = ({ organisationName = '', projects = [] }) => {
  const openExplainer = useOpenExplainer();

  const handleOpenExplainer = () => {
    openExplainer({ organisationName, projects });
  };

  return (
    <TestableButton
      dataTestId="ai-explainer-button"
      variant="outlined"
      size="sm"
      aria-haspopup="dialog"
      className="flex items-center gap-2 normal-case"
      onClick={handleOpenExplainer}
    >
      <LuSparkles aria-hidden="true" className="h-4 w-4" />
      AI Explainer
    </TestableButton>
  );
};

AIExplainerButton.propTypes = {
  organisationName: PropTypes.string,
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    title: PropTypes.string,
  })),
};

export default AIExplainerButton;